import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categoryInfo } from "@/mocks/catalog";
import { filterMovies } from "@/utils/data";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import MovieCard from "@/components/feature/MovieCard";
import FilterBar from "./components/FilterBar";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const info = categoryInfo.find((c) => c.key === type);

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("updated");
    setPage(1);
  }, [type]);

  usePageMeta({
    title: info ? `${info.name}分类 - 黄色在线` : "分类不存在 - 黄色在线",
    description: info
      ? `${info.name}分类页，${info.desc} 提供类型、地区、年份筛选与分页浏览。`
      : "未找到对应的影视分类。",
    keywords: `黄色在线,${info ? info.name : "影视分类"},影视资源,分类筛选`,
  });

  const list = useMemo(
    () => (info ? filterMovies({ type: info.key, genre, region, year, sort }) : []),
    [info, genre, region, year, sort]
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageList = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (patch: {
    genre?: string;
    region?: string;
    year?: string;
    sort?: string;
  }) => {
    if (patch.genre !== undefined) setGenre(patch.genre);
    if (patch.region !== undefined) setRegion(patch.region);
    if (patch.year !== undefined) setYear(patch.year);
    if (patch.sort !== undefined) setSort(patch.sort);
    setPage(1);
  };

  const handleReset = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort("updated");
    setPage(1);
  };

  if (!info) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <div className="mx-auto flex max-w-lg flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-10 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true" />
          <h1 className="font-heading text-xl font-bold text-foreground-950">暂无相关影视资源</h1>
          <p className="text-sm text-foreground-500">该分类不存在或已下线，请返回首页重新选择。</p>
          <Link
            to="/"
            className="mt-2 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
          >
            <i className="ri-home-4-line" aria-hidden="true" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="w-full bg-primary-900 px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb light items={[{ label: `${info.name}分类` }]} />
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
          {info.name}分类
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-background-300">{info.desc}</p>
      </section>

      <section className="w-full px-4 py-8 md:px-8 md:py-10">
        <FilterBar
          type={info.key}
          genre={genre}
          region={region}
          year={year}
          sort={sort}
          onChange={handleFilterChange}
        />

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共 <span className="font-semibold text-accent-600">{list.length}</span> 部作品
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-foreground-500 transition-colors hover:bg-background-200 hover:text-foreground-800"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            重置筛选
          </button>
        </div>

        {pageList.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {pageList.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-5 flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-12 text-center">
            <i className="ri-film-line text-4xl text-foreground-300" aria-hidden="true" />
            <h2 className="font-heading text-lg font-bold text-foreground-950">暂无相关影视资源</h2>
            <p className="text-sm text-foreground-500">没有符合当前筛选条件的作品，试试调整筛选。</p>
            <button
              type="button"
              onClick={handleReset}
              className="mt-1 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
            >
              <i className="ri-eraser-line" aria-hidden="true" />
              清除筛选条件
            </button>
          </div>
        )}

        {totalPages > 1 ? (
          <div className="mt-8">
            <Pagination
              current={page}
              total={totalPages}
              onChange={(p) => {
                setPage(p);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </div>
        ) : null}
      </section>
    </div>
  );
}